import React, { useMemo } from 'react';

import { Formik } from 'formik';

import { useProjectPlanReportContext } from './ProjectPlanReportContext';

import Button from 'CommonComponents/Button';
import DatePickerField from 'CommonComponents/DatePickerField';
import MultiSelectField from 'CommonComponents/MultiSelectField';
import SelectField from 'CommonComponents/SelectField';

const SCOPE_CENTRAL = 'central';

const DETAIL_SUMMARY = 'summary';
const DETAIL_DETAILED = 'detailed';

const STATUS_OVERDUE = 'overdue';

const selectClassName =
    'tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold placeholder:tw-text-charcoal-75 placeholder:tw-text-sm placeholder:tw-font-normal';

const dateInputClassName = 'tw-block tw-w-full tw-h-9 tw-px-3 tw-rounded-lg tw-text-sm tw-bg-grey/50 tw-text-charcoal tw-font-semibold tw-border-0';

const groupByOptions = () => [
    { value: 'category', label: $t('Category') },
    { value: 'owner', label: $t('Owner') },
    { value: 'status', label: $t('Status') },
    { value: 'scope', label: $t('Scope') },
    { value: 'time_period', label: $t('Time period') },
];

const timeViewOptions = () => [
    { value: 'days', label: $t('Days') },
    { value: 'months', label: $t('Months') },
    { value: 'quarters', label: $t('Quarters') },
    { value: 'years', label: $t('Years') },
];

const dateFieldOptions = () => [
    { value: 'target', label: $t('Target date') },
    { value: 'start', label: $t('Start date') },
    { value: 'estimated', label: $t('Estimated date') },
];

// The columns a user may show or hide on the detailed table.
const visibleColumnOptions = () => [
    { value: 'status', label: $t('Status') },
    { value: 'rag_status', label: $t('RAG') },
    { value: 'owner', label: $t('Owner') },
    { value: 'start_date', label: $t('Start date') },
    { value: 'target_completion_date', label: $t('Target date') },
    { value: 'estimated_completion_date', label: $t('Estimated date') },
    { value: 'variance', label: $t('Variance') },
];

const sortOptions = () => [
    { value: 'target_asc', label: $t('Target date (earliest first)') },
    { value: 'target_desc', label: $t('Target date (latest first)') },
    { value: 'variance_desc', label: $t('Variance (largest slip first)') },
];

const VIEW_TABLE = 'table';

// The on-screen view-modes (IB-674). Table is the tabular report; the visual
// modes render the detailed row set as a chart / exposure roll-up.
const viewModeOptions = () => [
    { value: VIEW_TABLE, label: $t('Table') },
    { value: 'timeline', label: $t('Timeline') },
    { value: 'gantt', label: $t('Gantt') },
    { value: 'exposure', label: $t('Milestone–Budget Exposure') },
];

/**
 * The Project Plan report builder controls (IB-671).
 *
 * Submitting the form pushes the assembled criteria into the report context,
 * which re-fetches the on-screen data. The structure mirrors the
 * Budgets-by-Dimension criteria form (Formik + the shared select fields).
 */
const ProjectPlanReportCriteria = () => {
    const { criteria, options, updateCriteria } = useProjectPlanReportContext();

    // Status filter adds the synthetic "overdue" option alongside the enum
    // statuses (matches CriteriaData::STATUS_OVERDUE on the backend).
    const statusOptions = useMemo(() => [...options.status, { value: STATUS_OVERDUE, label: $t('Overdue') }], [options.status]);

    const initialValues = useMemo(() => ({ ...criteria }), []);

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values, actions) => {
                updateCriteria(values);
                actions.setSubmitting(false);
            }}
        >
            {({ values, isSubmitting, handleSubmit, setFieldValue }) => {
                const isCentral = values.scope_level === SCOPE_CENTRAL;
                const isTable = (values.view_mode ?? VIEW_TABLE) === VIEW_TABLE;

                return (
                    <div className="tw-px-6 tw-py-7 tw-space-y-6 tw-rounded-lg tw-bg-background">
                        {/* Scope + entity */}
                        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-x-10 tw-gap-y-5">
                            <Field label={$t('Scope')}>
                                <SelectField
                                    className={selectClassName}
                                    options={options.scope}
                                    value={values.scope_level}
                                    backgroundColor="#e4e4e480"
                                    isDisabled={isSubmitting}
                                    placeholder={$t('Select scope')}
                                    onChange={(value) => {
                                        setFieldValue('scope_level', value ? value.value : SCOPE_CENTRAL);
                                        setFieldValue('scope_entity_ids', []);
                                    }}
                                />
                            </Field>

                            {!isCentral && (
                                <Field label={$t('Scope entities')}>
                                    <MultiSelectField
                                        className={selectClassName}
                                        options={(options.scopeEntitiesByLevel && options.scopeEntitiesByLevel[values.scope_level]) || []}
                                        value={values.scope_entity_ids}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        hideSelectedOptions={false}
                                        clearable
                                        placeholder={$t('All available')}
                                        onChange={(selected) => setFieldValue('scope_entity_ids', toValues(selected))}
                                    />
                                </Field>
                            )}

                            <Field label={$t('Categories')}>
                                <MultiSelectField
                                    className={selectClassName}
                                    options={options.category || []}
                                    value={values.category_ids}
                                    backgroundColor="#e4e4e480"
                                    isDisabled={isSubmitting}
                                    hideSelectedOptions={false}
                                    clearable
                                    placeholder={$t('All categories')}
                                    onChange={(selected) => setFieldValue('category_ids', toValues(selected))}
                                />
                            </Field>
                        </div>

                        {/* Filters */}
                        <div>
                            <label className="text-input__label tw-mb-2 tw-block">{$t('Filters')}</label>
                            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-x-10 tw-gap-y-5">
                                <Field label={$t('Statuses')}>
                                    <MultiSelectField
                                        className={selectClassName}
                                        options={statusOptions}
                                        value={values.statuses}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        hideSelectedOptions={false}
                                        clearable
                                        placeholder={$t('All statuses')}
                                        onChange={(selected) => setFieldValue('statuses', toValues(selected))}
                                    />
                                </Field>

                                <Field label={$t('Types')}>
                                    <MultiSelectField
                                        className={selectClassName}
                                        options={options.type}
                                        value={values.types}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        hideSelectedOptions={false}
                                        clearable
                                        placeholder={$t('All types')}
                                        onChange={(selected) => setFieldValue('types', toValues(selected))}
                                    />
                                </Field>

                                <Field label={$t('Owners')}>
                                    <MultiSelectField
                                        className={selectClassName}
                                        options={options.owner}
                                        value={values.owner_ids}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        hideSelectedOptions={false}
                                        clearable
                                        placeholder={$t('All owners')}
                                        onChange={(selected) => setFieldValue('owner_ids', toValues(selected))}
                                    />
                                </Field>

                                <Field label={$t('Tagged positions')}>
                                    <MultiSelectField
                                        className={selectClassName}
                                        options={options.position}
                                        value={values.tagged_position_ids}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        hideSelectedOptions={false}
                                        clearable
                                        placeholder={$t('All positions')}
                                        onChange={(selected) => setFieldValue('tagged_position_ids', toValues(selected))}
                                    />
                                </Field>

                                <Field label={$t('Date Field')}>
                                    <SelectField
                                        className={selectClassName}
                                        options={dateFieldOptions()}
                                        value={values.date_field}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        onChange={(value) => setFieldValue('date_field', value ? value.value : 'target')}
                                    />
                                </Field>

                                <div className="tw-grid tw-grid-cols-2 tw-gap-x-3">
                                    <Field label={$t('From')}>
                                        <DatePickerField
                                            name="date_from"
                                            className={dateInputClassName}
                                            value={values.date_from}
                                            valueFormat="yyyy-MM-dd"
                                            disabled={isSubmitting}
                                            onChange={(name, value) => setFieldValue('date_from', value || null)}
                                        />
                                    </Field>
                                    <Field label={$t('To')}>
                                        <DatePickerField
                                            name="date_to"
                                            className={dateInputClassName}
                                            value={values.date_to}
                                            valueFormat="yyyy-MM-dd"
                                            disabled={isSubmitting}
                                            onChange={(name, value) => setFieldValue('date_to', value || null)}
                                        />
                                    </Field>
                                </div>

                                <label className="tw-flex tw-items-center tw-gap-x-2 tw-text-sm tw-font-semibold tw-text-charcoal-50 lg:tw-self-end lg:tw-h-9">
                                    <input
                                        type="checkbox"
                                        disabled={isSubmitting}
                                        checked={!!values.exceptions_only}
                                        onChange={() => setFieldValue('exceptions_only', !values.exceptions_only)}
                                    />
                                    {$t('Exceptions only')}
                                </label>
                            </div>
                        </div>

                        {/* Structure */}
                        <div>
                            <label className="text-input__label tw-mb-2 tw-block">{$t('Structure')}</label>
                            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-x-10 tw-gap-y-5">
                                <Field label={$t('View')}>
                                    <SelectField
                                        className={selectClassName}
                                        options={viewModeOptions()}
                                        value={values.view_mode ?? VIEW_TABLE}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        onChange={(value) => setFieldValue('view_mode', value ? value.value : VIEW_TABLE)}
                                    />
                                </Field>

                                <Field label={$t('Group By')}>
                                    <SelectField
                                        className={selectClassName}
                                        options={groupByOptions()}
                                        value={values.group_by}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        onChange={(value) => setFieldValue('group_by', value ? value.value : 'category')}
                                    />
                                </Field>

                                {isTable && (
                                    <Field label={$t('Detail Level')}>
                                        <div className="tw-h-9 tw-flex tw-items-center tw-gap-x-5">
                                            <label className="tw-flex tw-items-center tw-gap-x-2 tw-text-sm tw-font-semibold tw-text-charcoal-50">
                                                <input
                                                    type="radio"
                                                    name="detail_level"
                                                    disabled={isSubmitting}
                                                    value={DETAIL_SUMMARY}
                                                    checked={values.detail_level === DETAIL_SUMMARY}
                                                    onChange={() => setFieldValue('detail_level', DETAIL_SUMMARY)}
                                                />
                                                {$t('Summary')}
                                            </label>
                                            <label className="tw-flex tw-items-center tw-gap-x-2 tw-text-sm tw-font-semibold tw-text-charcoal-50">
                                                <input
                                                    type="radio"
                                                    name="detail_level"
                                                    disabled={isSubmitting}
                                                    value={DETAIL_DETAILED}
                                                    checked={values.detail_level === DETAIL_DETAILED}
                                                    onChange={() => setFieldValue('detail_level', DETAIL_DETAILED)}
                                                />
                                                {$t('Detailed')}
                                            </label>
                                        </div>
                                    </Field>
                                )}

                                <Field label={$t('Time View')}>
                                    <SelectField
                                        className={selectClassName}
                                        options={timeViewOptions()}
                                        value={values.time_view}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        onChange={(value) => setFieldValue('time_view', value ? value.value : 'months')}
                                    />
                                </Field>

                                {isTable && (
                                    <Field label={$t('Visible Columns')}>
                                        <MultiSelectField
                                            className={selectClassName}
                                            options={visibleColumnOptions()}
                                            value={values.visible_columns || []}
                                            backgroundColor="#e4e4e480"
                                            isDisabled={isSubmitting || values.detail_level === DETAIL_SUMMARY}
                                            hideSelectedOptions={false}
                                            clearable
                                            placeholder={$t('All columns')}
                                            onChange={(selected) => setFieldValue('visible_columns', selected && selected.length ? toValues(selected) : null)}
                                        />
                                    </Field>
                                )}

                                <Field label={$t('Sort')}>
                                    <SelectField
                                        className={selectClassName}
                                        options={sortOptions()}
                                        value={values.sort}
                                        backgroundColor="#e4e4e480"
                                        isDisabled={isSubmitting}
                                        clearable
                                        placeholder={$t('Default order')}
                                        onChange={(value) => setFieldValue('sort', value ? value.value : null)}
                                    />
                                </Field>
                            </div>
                        </div>

                        <div className="tw-flex tw-justify-end">
                            <Button className="disabled:tw-bg-blue-25" variant="primary" onClick={handleSubmit} disabled={isSubmitting} loading={isSubmitting}>
                                {$t('Apply')}
                            </Button>
                        </div>
                    </div>
                );
            }}
        </Formik>
    );
};

/**
 * A labelled form control wrapper — keeps the label + control spacing
 * consistent with the sibling criteria forms.
 */
const Field = ({ label, children }) => (
    <div className="tw-flex tw-flex-col tw-space-y-1.5">
        <label className="text-input__label">{label}</label>
        {children}
    </div>
);

// react-select hands back an array of { value, label }; pull the values out.
const toValues = (selected) => (selected || []).map((option) => option.value);

export default ProjectPlanReportCriteria;
